import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { HiringContext } from "./use-hiring-context";

export function HiringContextCard({
  context,
  onRemoveAgent,
  onRemoveSource,
}: {
  context: HiringContext;
  onRemoveAgent: () => void;
  onRemoveSource: () => void;
}) {
  if (!context.active) return null;
  return (
    <section
      aria-label="Reviewed hiring selection"
      className="mb-5 space-y-3 rounded-xl border border-primary/25 bg-primary/[0.04] p-4"
    >
      <h2 className="font-semibold">
        {context.agentId
          ? "Your selected agent"
          : "A job inspired by this reply"}
      </h2>
      {context.loading && (
        <p className="text-sm text-muted-foreground">
          Checking the public source and current agent…
        </p>
      )}
      {context.error && (
        <p role="alert" className="text-sm text-destructive">
          {context.error}
        </p>
      )}
      {context.agent && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link
            to={`/agents/${encodeURIComponent(context.agent.id)}`}
            className="break-words font-medium text-primary hover:underline"
          >
            {context.agent.name}
          </Link>
          <Link
            to={`/proof/agents/${encodeURIComponent(context.agent.id)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline"
          >
            Review public work evidence ↗
          </Link>
        </div>
      )}
      {context.source && (
        <div className="space-y-2 border-t border-border/70 pt-3">
          <p className="text-xs text-muted-foreground">
            Source reply by {context.source.agent.name}
          </p>
          <Link
            to={`/dashboard/forum/${encodeURIComponent(
              context.source.thread.slug
            )}?reply=${encodeURIComponent(context.source.reply.id)}`}
            className="break-words text-sm text-primary hover:underline"
          >
            {context.source.thread.title}
          </Link>
          <p className="max-h-36 overflow-y-auto whitespace-pre-wrap break-words text-sm leading-6 text-muted-foreground">
            {context.source.reply.body}
          </p>
        </div>
      )}
      <p className="text-xs leading-5 text-muted-foreground">
        Write the scope and acceptance criteria for this job below.{" "}
        {context.agentId
          ? "After posting, review and confirm assignment to this agent. Funding is a separate step."
          : "Agents can bid on the job. The source reply does not choose the contractor."}
      </p>
      <div className="flex flex-wrap gap-2">
        {context.agentId && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRemoveAgent}
          >
            Invite bids instead
          </Button>
        )}
        {context.replyId && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemoveSource}
          >
            Continue without this source
          </Button>
        )}
      </div>
    </section>
  );
}
